// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/onboarding/calibrationBank.js
//  MICROSERVICIO ONBOARDING v3 — Banco de Minijuegos de Calibración
//
//  ⚠️  TRANSCRIPCIÓN LITERAL de docs/onboarding-master-v2.md, PARTE II.
//      25 preguntas = 5 niveles (A1, A2, B1, B2, C1) × 5 formatos.
//      "El agente no debe generar preguntas nuevas ni alterar el scoring"
//      (Parte III §5). Este archivo es datos, no lógica.
//
//  REGLA DE MEZCLA (Parte II §0): cada nivel usa los 5 formatos una vez, pero
//  en orden distinto al nivel anterior. El orden está codificado en el propio
//  arreglo `slides` de cada nivel — NO reordenar.
//
//  FORMATOS:
//    'order'    🔤 Ordena la frase        → sintaxis / estructura interna
//    'choice'   ✅ Opción múltiple        → vocabulario / gramática puntual
//    'listen'   🎧 Escucha y comprende    → comprensión auditiva general
//    'tone'     🗣️ Escucha y elige voces  → discriminación de tono/registro
//    'error'    🐛 Encuentra el error     → atención gramatical fina
//
//  AUDIO (Punto de Decisión B, Parte IV §2 — RESUELTO):
//    El campo `audio` es el texto que se sintetiza con Web Speech API nativa
//    del navegador (ver onboardingSpeech.js). Latencia ~0ms, sin costo, sin
//    tocar tts.py ni el pipeline REST Híbrido del tutor (Zona Roja).
//    `voice` ajusta rate/pitch para transmitir el tono que pide el documento.
// ═══════════════════════════════════════════════════════════════════════════════

export const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

export const CALIBRATION_BANK = {
  /* ═══════════════════════════════════════════════════════════════════════════
     NIVEL A1 — Básico absoluto
     Orden: Opción múltiple → Ordena la frase → Escucha y comprende →
            Encuentra el error → Escucha y elige entre voces
     ═══════════════════════════════════════════════════════════════════════════ */
  A1: [
    {
      id: 'A1-1',
      format: 'choice',
      prompt: '"My name ___ Ana."',
      options: [
        { id: 'a', text: 'is', correct: true },
        { id: 'b', text: 'are', correct: false },
        { id: 'c', text: 'am', correct: false },
        { id: 'd', text: 'be', correct: false },
      ],
      measures: 'verbo "to be", presente simple',
    },
    {
      id: 'A1-2',
      format: 'order',
      prompt: 'Ordena:',
      chips: ['is', 'book', 'this', 'my'],
      answer: ['This', 'is', 'my', 'book'],
      answerText: 'This is my book',
      measures: 'estructura básica sujeto-verbo-objeto',
    },
    {
      id: 'A1-3',
      format: 'listen',
      audio: 'I have two cats.',
      voice: { rate: 0.85, pitch: 1.0 },
      question: '¿Cuántas mascotas tiene?',
      options: [
        { id: 'a', text: 'Uno', correct: false },
        { id: 'b', text: 'Dos', correct: true },
        { id: 'c', text: 'Tres', correct: false },
        { id: 'd', text: 'Ninguna', correct: false },
      ],
      measures: 'comprensión de números y vocabulario básico',
    },
    {
      id: 'A1-4',
      format: 'error',
      words: ['She', 'have', 'a', 'red', 'car'],
      wrongIndex: 1,
      correction: 'has',
      measures: '3ra persona singular',
    },
    {
      id: 'A1-5',
      format: 'tone',
      audio: 'Thank you so much!',
      voice: { rate: 0.95, pitch: 1.25 }, // tono cálido
      question: '¿Cómo se siente la persona?',
      options: [
        { id: 'a', emoji: '😊', text: 'Agradecida', correct: true },
        { id: 'b', emoji: '😐', text: 'Indiferente', correct: false },
        { id: 'c', emoji: '😡', text: 'Molesta', correct: false },
      ],
      measures: 'reconocimiento de tono básico',
    },
  ],

  /* ═══════════════════════════════════════════════════════════════════════════
     NIVEL A2 — Básico
     Orden: Ordena la frase → Encuentra el error → Opción múltiple →
            Escucha y elige entre voces → Escucha y comprende
     ═══════════════════════════════════════════════════════════════════════════ */
  A2: [
    {
      id: 'A2-1',
      format: 'order',
      prompt: 'Ordena:',
      chips: ['to', 'going', 'I', 'am', 'the', 'park'],
      answer: ['I', 'am', 'going', 'to', 'the', 'park'],
      answerText: 'I am going to the park',
      measures: 'presente continuo con intención futura',
    },
    {
      id: 'A2-2',
      format: 'error',
      words: ['He', "don't", 'like', 'pizza'],
      wrongIndex: 1,
      correction: "doesn't",
      measures: 'negación en 3ra persona',
    },
    {
      id: 'A2-3',
      format: 'choice',
      prompt: '"There ___ many people at the party."',
      options: [
        { id: 'a', text: 'is', correct: false },
        { id: 'b', text: 'were', correct: true },
        { id: 'c', text: 'was', correct: false },
        { id: 'd', text: 'be', correct: false },
      ],
      measures: 'pasado simple con sustantivo plural',
    },
    {
      id: 'A2-4',
      format: 'tone',
      audio: 'Are you serious right now?',
      voice: { rate: 1.0, pitch: 1.35 }, // tono sorprendido/molesto
      question: '¿Cómo se siente la persona?',
      options: [
        { id: 'a', emoji: '😲', text: 'Sorprendida/molesta', correct: true },
        { id: 'b', emoji: '😊', text: 'Feliz', correct: false },
        { id: 'c', emoji: '😴', text: 'Aburrida', correct: false },
      ],
      measures: 'discriminación de tono conversacional',
    },
    {
      id: 'A2-5',
      format: 'listen',
      audio: 'We usually eat dinner at eight.',
      voice: { rate: 0.9, pitch: 1.0 },
      question: '¿A qué hora cenan normalmente?',
      options: [
        { id: 'a', text: 'A las 7', correct: false },
        { id: 'b', text: 'A las 8', correct: true },
        { id: 'c', text: 'A las 9', correct: false },
        { id: 'd', text: 'No lo dice', correct: false },
      ],
      measures: 'comprensión de horarios y rutinas',
    },
  ],

  /* ═══════════════════════════════════════════════════════════════════════════
     NIVEL B1 — Intermedio
     Orden: Escucha y comprende → Opción múltiple → Escucha y elige entre voces
            → Ordena la frase → Encuentra el error
     ═══════════════════════════════════════════════════════════════════════════ */
  B1: [
    {
      id: 'B1-1',
      format: 'listen',
      audio: "I've been working here for three years.",
      voice: { rate: 0.95, pitch: 1.0 },
      question: '¿Qué está expresando la persona?',
      options: [
        { id: 'a', text: 'Que empezó a trabajar ayer', correct: false },
        { id: 'b', text: 'Que lleva 3 años trabajando ahí, y sigue', correct: true },
        { id: 'c', text: 'Que ya no trabaja ahí', correct: false },
        { id: 'd', text: 'Que trabajará en 3 años', correct: false },
      ],
      measures: 'present perfect continuous',
    },
    {
      id: 'B1-2',
      format: 'choice',
      prompt: '"The ball is on your court." — ¿Qué significa?',
      options: [
        { id: 'a', text: 'Alguien está jugando tenis', correct: false },
        { id: 'b', text: 'Es tu turno de decidir o actuar', correct: true },
        { id: 'c', text: 'Alguien perdió una pelota', correct: false },
        { id: 'd', text: 'Se canceló un partido', correct: false },
      ],
      measures: 'comprensión de expresiones idiomáticas',
    },
    {
      id: 'B1-3',
      format: 'tone',
      audio: 'Oh great, ANOTHER meeting.',
      voice: { rate: 0.8, pitch: 0.85 }, // tono sarcástico
      question: '¿Cómo se siente realmente la persona?',
      options: [
        { id: 'a', emoji: '🙄', text: 'Fastidiada, aunque suena "positivo"', correct: true },
        { id: 'b', emoji: '😊', text: 'Emocionada', correct: false },
        { id: 'c', emoji: '😐', text: 'Neutral', correct: false },
      ],
      measures: 'detección de sarcasmo — habilidad clave de nivel B1+',
    },
    {
      id: 'B1-4',
      format: 'order',
      prompt: 'Ordena:',
      chips: ['had', 'I', 'more', 'time', 'if', 'would', 'travel', 'I'],
      answer: ['If', 'I', 'had', 'more', 'time,', 'I', 'would', 'travel'],
      answerText: 'If I had more time, I would travel',
      measures: 'condicional tipo 2',
    },
    {
      id: 'B1-5',
      format: 'error',
      words: ['I', 'am', 'living', 'here', 'since', '2019'],
      wrongIndex: 4,
      correction: 'have been living here since 2019 (falta el auxiliar "have been")',
      measures: 'present perfect continuous vs. presente simple mal usado',
    },
  ],

  /* ═══════════════════════════════════════════════════════════════════════════
     NIVEL B2 — Intermedio alto
     Orden: Escucha y elige entre voces → Encuentra el error →
            Escucha y comprende → Opción múltiple → Ordena la frase
     ═══════════════════════════════════════════════════════════════════════════ */
  B2: [
    {
      id: 'B2-1',
      format: 'tone',
      audio: "Honestly, I couldn't care less about the outcome.",
      voice: { rate: 0.9, pitch: 0.9 }, // indiferencia
      question: '¿Cuál es la actitud real de la persona?',
      options: [
        { id: 'a', emoji: '😐', text: 'Indiferencia total', correct: true },
        { id: 'b', emoji: '😢', text: 'Tristeza', correct: false },
        { id: 'c', emoji: '😊', text: 'Entusiasmo', correct: false },
      ],
      measures: 'comprensión de expresiones de indiferencia coloquial',
    },
    {
      id: 'B2-2',
      format: 'error',
      words: ['By', 'the', 'time', 'we', 'arrive,', 'the', 'movie', 'already started'],
      wrongIndex: 7,
      correction: 'will have already started',
      measures: 'futuro perfecto en cláusulas temporales',
    },
    {
      id: 'B2-3',
      format: 'listen',
      audio: "Had I known about the traffic, I would've left earlier.",
      voice: { rate: 0.95, pitch: 1.0 },
      question: '¿Qué pasó realmente?',
      options: [
        { id: 'a', text: 'Salió temprano y evitó el tráfico', correct: false },
        { id: 'b', text: 'No sabía del tráfico y salió tarde', correct: true },
        { id: 'c', text: 'Sabía del tráfico y decidió esperar', correct: false },
        { id: 'd', text: 'No hubo tráfico', correct: false },
      ],
      measures: 'condicional tipo 3 con inversión — estructura avanzada',
    },
    {
      id: 'B2-4',
      format: 'choice',
      prompt: '"___ the rain, they decided to go for a walk."',
      options: [
        { id: 'a', text: 'Despite of', correct: false },
        { id: 'b', text: 'Despite', correct: true },
        { id: 'c', text: 'Although', correct: false },
        { id: 'd', text: 'In spite', correct: false },
      ],
      measures: 'conectores concesivos — uso correcto de preposición vs. conjunción',
    },
    {
      id: 'B2-5',
      format: 'order',
      prompt: 'Ordena:',
      chips: ['managed', 'despite', 'the', 'team', 'several', 'setbacks', 'to', 'deliver', 'having', 'faced', 'on', 'time'],
      answer: ['Despite', 'having', 'faced', 'several', 'setbacks,', 'the', 'team', 'managed', 'to', 'deliver', 'on', 'time'],
      answerText: 'Despite having faced several setbacks, the team managed to deliver on time',
      measures: 'estructura concesiva compleja con participio compuesto',
    },
  ],

  /* ═══════════════════════════════════════════════════════════════════════════
     NIVEL C1 — Avanzado
     Orden: Encuentra el error → Escucha y comprende → Ordena la frase →
            Escucha y elige entre voces → Opción múltiple
     ═══════════════════════════════════════════════════════════════════════════ */
  C1: [
    {
      id: 'C1-1',
      format: 'error',
      words: ['Not only', 'she was late,', 'but she also', 'forgot', 'the documents'],
      wrongIndex: 1,
      correction: 'was she late (inversión obligatoria tras "Not only" al inicio de frase)',
      measures: 'inversión enfática — estructura de registro alto',
    },
    {
      id: 'C1-2',
      format: 'listen',
      audio: "It's not that I don't value your input — it's more that the timing couldn't be worse.",
      voice: { rate: 0.95, pitch: 1.0 },
      question: '¿Qué está comunicando realmente la persona?',
      options: [
        { id: 'a', text: 'Que no le importa la opinión del otro', correct: false },
        { id: 'b', text: 'Que sí valora la opinión, pero el momento es inoportuno', correct: true },
        { id: 'c', text: 'Que quiere cancelar el proyecto', correct: false },
        { id: 'd', text: 'Que está de acuerdo sin condiciones', correct: false },
      ],
      measures: 'comprensión de matices y diplomacia lingüística',
    },
    {
      id: 'C1-3',
      format: 'order',
      prompt: 'Ordena:',
      // [PARCHE v2.2] El documento maestro corrigio las fichas de este slide:
      // faltaba el articulo 'a', sin el cual la respuesta correcta era
      // imposible de armar. Ahora las fichas cubren la frase completa.
      chips: ['rarely', 'have', 'I', 'seen', 'such', 'a', 'level', 'of', 'dedication'],
      answer: ['Rarely', 'have', 'I', 'seen', 'such', 'a', 'level', 'of', 'dedication'],
      answerText: 'Rarely have I seen such a level of dedication',
      measures: 'inversión con adverbios de frecuencia negativa — registro formal/literario',
    },
    {
      id: 'C1-4',
      format: 'tone',
      audio: "Well, that's certainly... one way to put it.",
      voice: { rate: 0.75, pitch: 0.95 }, // tono contenido, pausa marcada
      question: '¿Qué está realmente pensando la persona?',
      options: [
        { id: 'a', emoji: '🤨', text: 'Está en desacuerdo, pero lo dice con diplomacia', correct: true },
        { id: 'b', emoji: '😊', text: 'Está totalmente de acuerdo', correct: false },
        { id: 'c', emoji: '😴', text: 'Le da igual', correct: false },
      ],
      measures: 'lectura de subtexto y cortesía pasivo-agresiva — matiz muy avanzado',
    },
    {
      id: 'C1-5',
      format: 'choice',
      prompt: '¿Cuál suena más natural en un correo formal de negocios?',
      options: [
        { id: 'a', text: '"I wanna know if..."', correct: false },
        { id: 'b', text: '"Can u tell me..."', correct: false },
        { id: 'c', text: '"I would appreciate it if you could clarify..."', correct: true },
        { id: 'd', text: '"Tell me pls..."', correct: false },
      ],
      measures: 'adecuación de registro formal — habilidad C1 clave',
    },
  ],
};

/**
 * Nivel de arranque de la calibración según la autopercepción de Pantalla 4.
 *
 * El documento fija la bifurcación de "cero absoluto" (salta la prueba) pero no
 * el nivel de entrada de los demás. Arrancamos siempre en A1 porque el corte
 * adaptativo de Parte II §1 ya resuelve la velocidad: quien va sobrado barre
 * A1/A2 en segundos y el motor lo empuja hacia arriba solo. Entrar más arriba
 * rompería la promesa de honestidad ("no castigar con preguntas por encima").
 */
export const START_LEVEL = 'A1';
